import { useState } from 'react'

import {
  type PlanSubmissionKind,
  PlanTimeframe,
} from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import type { NoticedPlan } from '@ebalance/proto/bffuser/v1/plan_auto_submit_notice_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Stack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { Modal } from '~/components/common/Modal'
import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'
import {
  type PlanAutoSubmitNotices,
  asksToSubmitAny,
  formatMissedKomas,
  planAutoSubmitNoticeReasonText,
  planAutoSubmitNoticesOfKind,
} from '~/models/planAutoSubmitNoticeRows'

const PREFIX = 'common:planAutoSubmitNotice'

/** 値が無い欄 (BFF が未知の値を落とした時) の表示。 */
const EMPTY_CELL = '—'

const TIMEFRAME_KEYS: Record<PlanTimeframe, string | undefined> = {
  [PlanTimeframe.UNSPECIFIED]: undefined,
  [PlanTimeframe.TODAY]: 'today',
  [PlanTimeframe.TOMORROW]: 'tomorrow',
  [PlanTimeframe.DAY_AFTER_TOMORROW]: 'dayAfterTomorrow',
  [PlanTimeframe.WEEKLY]: undefined,
  [PlanTimeframe.MONTHLY]: undefined,
  [PlanTimeframe.ANNUAL]: undefined,
}

type DetailRow = {
  key: string
  plan: NoticedPlan | undefined
  /** 最後の列 (理由、または出し直せなかったコマ) の表示文言。 */
  detail: string
}

/**
 * 行の key。BFF が未知の enum を UNSPECIFIED に落とした行どうしで計画の識別子が衝突しうるので、
 * 行の index を含める (server は計画ごとに 1 行を返すので、表示順は変わらない)。
 */
const planKey = (plan: NoticedPlan | undefined, index: number): string =>
  plan === undefined
    ? `${index}:`
    : [
        index,
        plan.kind,
        plan.timeframe,
        plan.targetDate === undefined
          ? ''
          : `${plan.targetDate.year}-${plan.targetDate.month}-${plan.targetDate.day}`,
        plan.areaCode,
        plan.submissionEntityCode,
      ].join(':')

const detailColumns = (
  t: TFunction,
): Array<MasterDataTableColumn<DetailRow>> => [
  {
    key: 'targetDate',
    header: t(`${PREFIX}.column.targetDate`),
    nowrap: true,
    cell: (row) =>
      row.plan?.targetDate === undefined
        ? EMPTY_CELL
        : formatDateDisplay(fromProtoDate(row.plan.targetDate)),
  },
  {
    key: 'timeframe',
    header: t(`${PREFIX}.column.timeframe`),
    nowrap: true,
    cell: (row) => {
      const key =
        row.plan === undefined ? undefined : TIMEFRAME_KEYS[row.plan.timeframe]
      return key === undefined ? EMPTY_CELL : t(`${PREFIX}.timeframe.${key}`)
    },
  },
  {
    key: 'area',
    header: t(`${PREFIX}.column.area`),
    nowrap: true,
    cell: (row) =>
      row.plan === undefined ? EMPTY_CELL : convertAreaLabel(row.plan.areaCode),
  },
  {
    key: 'submissionEntityCode',
    header: t(`${PREFIX}.column.submissionEntityCode`),
    nowrap: true,
    cell: (row) =>
      row.plan === undefined || row.plan.submissionEntityCode === ''
        ? EMPTY_CELL
        : row.plan.submissionEntityCode,
  },
  {
    key: 'detail',
    header: t(`${PREFIX}.column.reason`),
    cell: (row) => (row.detail === '' ? EMPTY_CELL : row.detail),
  },
]

type BannerProps = {
  title: string
  body: string | undefined
  detailsTitle: string
  rows: readonly DetailRow[]
}

/**
 * 警告バナー 1 つと、「詳細を見る」で開くダイアログ (design `_mountOnePlanAutoSubmitBanner`)。
 * 行が無ければ何も描画しない。バナーに閉じる操作は持たない (design `data-alert-persist`)。
 * 計画はバナーに並べず、ダイアログに上限なしで出す (`conventions.md`「警告・案内で対象を列挙する時の
 * 件数と上限」)。
 */
const PlanAutoSubmitNoticeBanner = ({
  title,
  body,
  detailsTitle,
  rows,
}: BannerProps): React.ReactElement | null => {
  const { t } = useTranslation()
  const [detailsOpen, setDetailsOpen] = useState(false)
  if (rows.length === 0) return null
  return (
    <>
      {/* [DS差異] アイコンの縦位置: DS `Alert` の既定 (上下中央) でなく見出しの行に揃える
          (design `_mountOnePlanAutoSubmitBanner` の `align-items:flex-start` とアイコンの
          `margin-top:2px`)。DS `Alert` はアイコンに props を渡せないので子セレクタで当てる。
          `flexShrink={0}`: 高さ拘束された column flex の通知の器でも押し潰されないようにする
          (DS `Alert` は `overflow: hidden`)。 */}
      <Alert
        flexShrink={0}
        css={{ '& > svg': { alignSelf: 'flex-start', marginTop: '2px' } }}
        description={body}
        status="warning"
        title={title}
      >
        <AlertActions>
          <TertiaryButton onClick={() => setDetailsOpen(true)} size="sm">
            {t(`${PREFIX}.showDetails`)}
          </TertiaryButton>
        </AlertActions>
      </Alert>
      <Modal
        footer={
          <TertiaryButton onClick={() => setDetailsOpen(false)} size="md">
            {t('common:action.close')}
          </TertiaryButton>
        }
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        size="xl"
        title={detailsTitle}
      >
        <Stack gap="3">
          <Text data-eb-alert-detail-count>
            {t(`${PREFIX}.detailsCount`, { count: rows.length })}
          </Text>
          <MasterDataTable
            columns={detailColumns(t)}
            getRowKey={(row) => row.key}
            rows={[...rows]}
          />
        </Stack>
      </Modal>
    </>
  )
}

type Props = {
  /** layout が取得した知らせ (全計画種別)。部品が `planKind` の分だけに絞る。 */
  notices: PlanAutoSubmitNotices
  /** 出す計画種別。絞り忘れを型で防ぐため、絞り込みは部品が持つ。 */
  planKind: PlanSubmissionKind
}

/**
 * 計画一覧の警告バナー 2 つ (AC-PPL-035 / AC-PLC-082): 「自動で提出できていない計画」→
 * 「締切までに出し直せなかった計画」の順 (design `_mountPlanAutoSubmitSkipBanner`)。
 * 見出しの {N} は `planKind` で絞った後の計画の数で、選択中の対象日・フィルタでは絞らない。
 * 行の並びは server の順のまま。どちらも空でも同じ要素の木を返す (ページの通知の器が
 * 出入りで本文を作り直さないようにする。`PageNoticeFrame`)。
 */
export const PlanAutoSubmitNoticeBanners = ({
  notices,
  planKind,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { unsubmitted, missedDeadline } = planAutoSubmitNoticesOfKind(
    notices,
    planKind,
  )
  const unsubmittedRows: DetailRow[] = unsubmitted.map((n, i) => ({
    key: planKey(n.plan, i),
    plan: n.plan,
    detail: planAutoSubmitNoticeReasonText(t, n.reason) ?? '',
  }))
  const missedDeadlineRows: DetailRow[] = missedDeadline.map((n, i) => ({
    key: planKey(n.plan, i),
    plan: n.plan,
    detail: formatMissedKomas(t, n.missedKomas),
  }))
  return (
    <>
      <PlanAutoSubmitNoticeBanner
        body={
          asksToSubmitAny(unsubmitted)
            ? t(`${PREFIX}.unsubmitted.body`)
            : undefined
        }
        detailsTitle={t(`${PREFIX}.unsubmitted.detailsTitle`)}
        rows={unsubmittedRows}
        title={t(`${PREFIX}.unsubmitted.title`, {
          count: unsubmittedRows.length,
        })}
      />
      <PlanAutoSubmitNoticeBanner
        body={t(`${PREFIX}.missedDeadline.body`)}
        detailsTitle={t(`${PREFIX}.missedDeadline.detailsTitle`)}
        rows={missedDeadlineRows}
        title={t(`${PREFIX}.missedDeadline.title`, {
          count: missedDeadlineRows.length,
        })}
      />
    </>
  )
}
